import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTimer } from 'react-timer-hook';
import { array, number, object } from 'yup';

import { useToast } from 'Component/Alerta';
import { AnimacaoErro, AnimacaoParabens } from 'Component/Animacao';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, { CampoOculto, Radio } from 'Component/Form';
import Imagem from 'Component/Imagem';
import AvaliacaoRespondida, {
  RetornoAvaliacaoRespondida,
} from 'Interface/AvaliacoesRespondidas';
import Curso from 'Interface/Cursos';
import {
  useAdicionarAvaliacaoRespondidaMutation,
  useRecuperarAvaliacaoQuery,
  useRecuperarAvaliacoesQuery,
  useRecuperarCursosQuery,
} from 'Service';

const regras = object({
  respostas: array().of(
    object().shape({
      alternativa_id: number().required('Selecione alguma alternativa'),
    })
  ),
});

const Adicionar = (): React.ReactElement => {
  const limiteDeTempo = new Date();
  limiteDeTempo.setSeconds(limiteDeTempo.getSeconds() + 60 * 60);

  const { slug = '', id = 0 } = useParams();
  const [nota, setNota] = useState<number>();
  const [passou, setPassou] = useState<boolean>(false);
  const [reprovou, setReprovou] = useState<boolean>(false);
  const toast = useToast();
  const [adicionarAvaliacao] = useAdicionarAvaliacaoRespondidaMutation();
  const { seconds, minutes, hours, pause, resume } = useTimer({
    expiryTimestamp: limiteDeTempo,
    onExpire: () => console.warn('onExpire called'),
  });

  const { data: { cursos: [curso] } = { cursos: [{} as Curso] } } =
    useRecuperarCursosQuery({
      finder: 'slug',
      contain: ['Categorias', 'Modulos.Aulas.AulaConcluida'],
      options: { slug: { slug: slug } },
    });
  const { data: { avaliacao } = {} } = useRecuperarAvaliacaoQuery({
    id: id,
    contain: ['Questoes.Alternativas'],
  });
  const { data: { avaliacoes } = {}, isFetching: buscandoAvaliacoes } =
    useRecuperarAvaliacoesQuery(
      {
        finder: 'proximas',
        options: { curso_id: curso.id },
      },
      { skip: !curso.id }
    );

  const enviar = async (dados: AvaliacaoRespondida): Promise<void> => {
    if (avaliacao) {
      pause();

      try {
        const resultado = (await adicionarAvaliacao({
          ...dados,
          curso_id: curso.id,
          modulo_id: avaliacao.modulo_id,
        }).unwrap()) as unknown as RetornoAvaliacaoRespondida;

        if (resultado.avaliacao_respondida.status == 1) {
          setPassou(true);
        } else {
          setReprovou(true);
        }

        setNota(resultado.avaliacao_respondida.nota);
      } catch (error) {
        toast({
          titulo: 'Erro ao corrigir sua prova',
          conteudo: 'Verifique os logs para maiores detalhes.',
          tema: 'danger',
        });

        resume();
      }
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <div
        className="hf-400 d-flex align-items-end"
        style={{
          backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 100%), url(${curso.imagem})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          marginTop: '-130px',
        }}
      >
        <div className="container text-center">
          <h1 className="display-2 text-white">
            <small className="d-block text-secondary fs-3 fw-bold">
              Avaliação de
            </small>
            {curso.nome}
          </h1>
          <p className="lead text-muted">
            Você possui o total de 1 hora para realização de sua prova. Tenha
            calma, respire fundo e boa sorte!
          </p>
        </div>
      </div>
      <div className="container mb-5 mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="rounded px-3 bg-paper">
              <div className="row">
                <div className="col-1 border-end border-danger bg-paper-dots" />
                <div className="col-11">
                  <div
                    className="d-flex justify-content-center position-sticky mb-5 mt-3 z-index-1000"
                    style={{ top: '20px' }}
                  >
                    <div className="bg-dark rounded shadow-lg fs-1 text-white px-3">
                      {hours.toString().padStart(2, '0')}:
                      {minutes.toString().padStart(2, '0')}:
                      {seconds.toString().padStart(2, '0')}
                    </div>
                  </div>
                  <div>
                    <Form onSubmit={enviar} regras={regras}>
                      <React.Fragment>
                        {avaliacao?.questoes?.map((questao, indiceQuestao) => (
                          <div
                            key={`${questao.id}-${questao.modificado}`}
                            className={indiceQuestao ? 'mt-5' : ''}
                          >
                            <p className="fs-4 fw-light">
                              <span className="fw-bold">
                                #{indiceQuestao + 1}
                              </span>{' '}
                              - {questao.enunciado}
                            </p>
                            {questao.imagem && (
                              <Imagem
                                alt={questao.enunciado}
                                src={questao.imagem}
                                className="img-fluid d-block rounded-3 mb-3 mx-auto"
                              />
                            )}
                            <p className="fs-5 fw-bold">Alternativas</p>
                            {questao.alternativas?.map(
                              (alternativa, indiceAlternativa) => (
                                <div
                                  key={`${alternativa.id}-${alternativa.modificado}`}
                                  className="fs-5 fw-light"
                                >
                                  <CampoOculto
                                    name={`respostas.${indiceQuestao}.questao_id`}
                                    value={questao.id}
                                  />
                                  <Radio
                                    name={`respostas.${indiceQuestao}.alternativa_id`}
                                    value={alternativa.id}
                                    rotulo={`${indiceAlternativa + 1}. ${
                                      alternativa.texto
                                    }`}
                                  />
                                </div>
                              )
                            )}
                          </div>
                        ))}
                        <div className="mb-3 mt-5 text-center">
                          <Botao lg type="submit">
                            Terminar avaliação
                          </Botao>
                        </div>
                      </React.Fragment>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {passou && (
        <React.Fragment>
          <div className="modal show animate__animated animate__fadeIn animate__faster d-block">
            <div className="position-absolute top-50 start-50 translate-middle text-center">
              <p className="display-1 text-white">Parabéns!</p>
              <p className="lead text-white">
                Você atingiu a{' '}
                <span className="fw-bold text-success">nota {nota}</span> em sua
                avaliação e foi aprovado!
              </p>
              {!buscandoAvaliacoes && (
                <React.Fragment>
                  {avaliacoes?.length ? (
                    <React.Fragment>
                      <p className="lead text-white">
                        Ainda falta{' '}
                        <span className="fw-bold text-secondary">
                          {avaliacoes.length} avaliação(ões)
                        </span>{' '}
                        para você realizar desse curso. Continue estudando e se
                        dedicando para conseguir seu certificado de conclusão!
                      </p>
                      <div className="d-block mt-5">
                        <BotaoLink lg to={`/estudar/curso/${curso.slug}`}>
                          Voltar para o curso
                        </BotaoLink>
                      </div>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <p className="lead text-white">
                        Para continuar você deve escolher como deseja seu{' '}
                        <span className="fw-bold text-secondary">
                          certificado
                        </span>
                        . Você pode optar somente pela via digital, sem custo
                        algum, ou optar pela via física impressa que possui uma
                        taxa de emissão e envio.
                      </p>
                      <div className="hstack gap-3 mx-auto justify-content-center">
                        <BotaoLink
                          lg
                          to={`/estudar/curso/${curso.slug}/certificado/digital`}
                        >
                          <React.Fragment>
                            Somente via digital
                            <br />
                            <small className="fs-6 fw-lighter">
                              (Sem custo)
                            </small>
                          </React.Fragment>
                        </BotaoLink>
                        <BotaoLink
                          lg
                          tema="secondary"
                          to={`/estudar/curso/${curso.slug}/certificado/fisico`}
                        >
                          <React.Fragment>
                            Quero também a via física
                            <br />
                            <small className="fs-6 fw-lighter">
                              (Possui custo)
                            </small>
                          </React.Fragment>
                        </BotaoLink>
                      </div>
                      <div className="d-block mt-5">
                        <BotaoLink
                          lg
                          to="/estudar/meus-estudos"
                          tema="outline-light"
                        >
                          Decidir mais tarde
                        </BotaoLink>
                      </div>
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
            </div>
            <AnimacaoParabens />
          </div>
          <div className="modal-backdrop show animate__animated animate__fadeIn animate__faster"></div>
        </React.Fragment>
      )}
      {reprovou && (
        <React.Fragment>
          <div className="modal show animate__animated animate__fadeIn animate__faster d-block">
            <div className="position-absolute top-50 start-50 translate-middle text-center">
              <div className="d-flex justify-content-center">
                <AnimacaoErro />
              </div>
              <p className="display-1 text-white">Não foi dessa vez...</p>
              <p className="lead text-white">
                Você tirou a nota{' '}
                <span className="fw-bold text-danger">nota {nota}</span> e
                infelizmente não atingiu a média necessária para poder ser
                aprovado na avaliação.
              </p>
              <p className="lead text-white">
                Mas não se preocupe, você ainda pode tentar novamente! Estude
                bastante e volta mais tarde para tentar novamente, ou, se
                preferir, pode tentar agora.
              </p>
              <div className="hstack gap-3 mx-auto justify-content-center">
                <BotaoLink lg to="/estudar/meus-cursos">
                  <React.Fragment>
                    Tentar novamente mais tarde
                    <br />
                    <small className="fs-6 fw-lighter">(Recomentado)</small>
                  </React.Fragment>
                </BotaoLink>
                <BotaoLink
                  lg
                  tema="outline-light"
                  to={`/estudar/curso/${curso.slug}/avaliacao/processar`}
                >
                  <React.Fragment>
                    Tentar agora
                    <br />
                    <small className="fs-6 fw-lighter">(Não recomendado)</small>
                  </React.Fragment>
                </BotaoLink>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show animate__animated animate__fadeIn animate__faster"></div>
        </React.Fragment>
      )}
    </div>
  );
};

export default Adicionar;
